/**
 * Mode « données de test » pour la borne.
 *
 * Activé via la variable d'environnement `VITE_KIOSK_MOCK=true`.
 * Permet de dérouler tout le parcours de pari sans dépendre de la vraie API PMU.
 *
 * Réglages optionnels (fichier `.env` / `.env.local`) :
 *  - VITE_KIOSK_MOCK=true            → active le mode mock
 *  - VITE_KIOSK_MOCK_COURSE=1        → n° de la course rendue « pariable » (1 à 7)
 *  - VITE_KIOSK_MOCK_REMAINING=8     → minutes restantes avant le départ au chargement
 *
 * Les horaires du jeu de données (réunion R2 du 22/07/2026) sont recalés
 * dynamiquement par rapport à `Date.now()`, si bien qu'une course est toujours
 * dans la fenêtre de pari — et que le compte à rebours s'écoule réellement,
 * ce qui permet aussi de tester la fermeture des paris en fin de timer.
 */
import raw from './reunion.json'
import type { ActiveCourseResponse, BetPayload, BetTypeOption, Course, GenyPronosticResponse, KioskBet } from '../type'

const WINDOW_MINUTES = 25

/** Référentiel mock des types de paris (miroir de l'enum back KioskBetType). */
const MOCK_BET_TYPES: Array<Omit<BetTypeOption, 'min_partants'> & { min_partants: number }> = [
    { value: 'simple_gagnant', pmc_code: 1, display_name: 'Vainqueur', official_name: 'Simple gagnant', description: 'Votre cheval doit arriver 1er de la course.', horses_count: 1, is_ordered: false, min_partants: 2, emoji: '🏆', color: 'green', stars: 3 },
    { value: 'simple_place', pmc_code: 3, display_name: 'Top 3', official_name: 'Simple placé', description: 'Votre cheval doit finir dans les 3 premiers (1er, 2e ou 3e).', horses_count: 1, is_ordered: false, min_partants: 2, emoji: '🥉', color: 'green', stars: 4 },
    { value: 'couple_gagnant', pmc_code: 13, display_name: 'Duo Gagnant', official_name: 'Couplé gagnant', description: 'Vos 2 chevaux doivent finir 1er et 2e (peu importe l\'ordre).', horses_count: 2, is_ordered: false, min_partants: 8, emoji: '👥', color: 'orange', stars: 2 },
    { value: 'couple_place', pmc_code: 14, display_name: 'Duo Top 3', official_name: 'Couplé placé', description: 'Vos 2 chevaux doivent finir parmi les 3 premiers.', horses_count: 2, is_ordered: false, min_partants: 8, emoji: '🥈', color: 'orange', stars: 3 },
    { value: 'couple_ordre', pmc_code: 32, display_name: 'Duo Gagnant ordre exact', official_name: 'Couplé ordre', description: 'Vos 2 chevaux doivent finir 1er et 2e dans l\'ordre exact.', horses_count: 2, is_ordered: true, min_partants: 2, emoji: '🎯', color: 'orange', stars: 1 },
    { value: 'deux_sur_quatre', pmc_code: 8, display_name: 'Duo Top 4', official_name: '2 sur 4', description: 'Vos 2 chevaux doivent finir parmi les 4 premiers (peu importe l\'ordre).', horses_count: 2, is_ordered: false, min_partants: 10, emoji: '🎲', color: 'orange', stars: 2 },
    { value: 'trio', pmc_code: 33, display_name: 'Trio Top 3', official_name: 'Trio', description: 'Vos 3 chevaux doivent finir 1er, 2e et 3e (peu importe l\'ordre).', horses_count: 3, is_ordered: false, min_partants: 8, emoji: '🐎', color: 'red', stars: 2 },
    { value: 'trio_ordre', pmc_code: 45, display_name: 'Trio ordre exact', official_name: 'Trio ordre', description: 'Vos 3 chevaux doivent finir 1er, 2e et 3e dans l\'ordre exact.', horses_count: 3, is_ordered: true, min_partants: 3, emoji: '🎯', color: 'red', stars: 1 },
    { value: 'quarte', pmc_code: 17, display_name: 'Top 4', official_name: 'Top 4', description: 'Vos 4 chevaux doivent occuper les 4 premières places (peu importe l\'ordre).', horses_count: 4, is_ordered: false, min_partants: 10, emoji: '🏇', color: 'purple', stars: 1 },
    { value: 'quarte_ordre', pmc_code: 17, display_name: 'Top 4 ordre exact', official_name: 'Top 4 ordre', description: 'Vos 4 chevaux doivent finir aux 4 premières places dans l\'ordre exact.', horses_count: 4, is_ordered: true, min_partants: 5, emoji: '🎯', color: 'purple', stars: 1 },
    { value: 'quinte', pmc_code: 36, display_name: 'Top 5', official_name: 'Top 5', description: 'Vos 5 chevaux doivent occuper les 5 premières places (peu importe l\'ordre).', horses_count: 5, is_ordered: false, min_partants: 12, emoji: '⭐', color: 'blue', stars: 1 },
]

/** Filtre le référentiel selon le nombre de partants effectifs (hors non-partants). */
function mockAvailableBetTypes(course: Course): BetTypeOption[] {
    const effective = (course.partants ?? []).filter((p) => !p.non_partant).length
    return MOCK_BET_TYPES.filter((o) => {
        if (o.min_partants == null) return false
        return effective >= o.min_partants
    })
}

interface RawReunion {
    reunion: string
    source_url: string
    courses: Course[]
}

const data = raw as unknown as RawReunion

/** Le mode mock est-il activé ? */
export const isMockEnabled = String(import.meta.env.VITE_KIOSK_MOCK ?? '').toLowerCase() === 'true'

function config() {
    const rawIndex = Number(import.meta.env.VITE_KIOSK_MOCK_COURSE ?? 1)
    const remaining = Number(import.meta.env.VITE_KIOSK_MOCK_REMAINING ?? 8)
    const courseIndex = Math.min(Math.max(Number.isFinite(rawIndex) ? rawIndex : 1, 1), data.courses.length)
    return {
        courseIndex,
        remainingMinutes: Number.isFinite(remaining) ? remaining : 8,
    }
}

// Décalage fixé une seule fois au chargement : le compte à rebours diminue ainsi
// au fil du temps, comme avec la vraie API.
let anchorMs: number | null = null

function shiftedCourses(): Course[] {
    const { courseIndex, remainingMinutes } = config()
    const target = data.courses[courseIndex - 1]

    if (anchorMs === null) {
        const targetDepart = target.heure_depart_ms ?? Date.now()
        // On veut : target.heure_depart_ms => maintenant + remainingMinutes.
        anchorMs = Date.now() + remainingMinutes * 60_000 - targetDepart
    }

    const delta = anchorMs
    return data.courses.map((c) => ({
        ...c,
        heure_depart_ms: c.heure_depart_ms === null ? null : c.heure_depart_ms + delta,
    }))
}

/** Simule GET /kiosk/active-course. */
export function mockActiveCourse(): ActiveCourseResponse {
    const { courseIndex } = config()
    const courses = shiftedCourses()
    const now = Date.now()
    const windowMs = WINDOW_MINUTES * 60_000

    const target = courses[courseIndex - 1]
    const depart = target.heure_depart_ms ?? 0
    const isOpen = depart - windowMs <= now && now < depart

    if (isOpen) {
        return {
            success: true,
            is_open: true,
            window_minutes: WINDOW_MINUTES,
            reunion: data.reunion,
            reunion_id: 'R2',
            date: '22072026',
            hippodrome_code: 'CLA',
            course: target,
            available_bet_types: mockAvailableBetTypes(target),
            tenant: {
                name: 'Hippodrome de Clairefontaine',
                logo_borne: '/img/v2/logo.png',
                logo_mini: '/img/v2/logo-mini.png',
            },
        }
    }

    // Course terminée ou pas encore ouverte : on tease la prochaine.
    const upcoming = courses
        .filter((c) => (c.heure_depart_ms ?? 0) > now)
        .sort((a, b) => (a.heure_depart_ms ?? 0) - (b.heure_depart_ms ?? 0))[0]

    return {
        success: true,
        is_open: false,
        window_minutes: WINDOW_MINUTES,
        reunion: data.reunion,
        message: upcoming
            ? 'Patience, une nouvelle course arrive bientôt !'
            : 'Aucune course à venir pour le moment.',
        next_course: upcoming
            ? {
                nom: upcoming.nom,
                heure: upcoming.heure,
                heure_ouverture: formatTimeFromMs((upcoming.heure_depart_ms ?? 0) - windowMs),
                heure_depart_ms: upcoming.heure_depart_ms,
                minutes_until_open: Math.max(
                    0,
                    Math.ceil(((upcoming.heure_depart_ms ?? 0) - windowMs - now) / 60_000),
                ),
            }
            : null,
        tenant: {
            name: 'Hippodrome de Clairefontaine',
            logo_borne: '/img/v2/logo_v2.png',
            logo_mini: '/img/v2/logo-mini.png',
        },
    }
}

function formatTimeFromMs(timestampMs: number): string {
    return new Date(timestampMs).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

/** Simule GET /kiosk/program. */
export function mockProgram() {
    return {
        reunion: data.reunion,
        reunion_id: 'R2',
        date: '22072026',
        courses: shiftedCourses(),
    }
}

/** Simule POST /kiosk/bets. */
export function mockStoreBet(payload: BetPayload): KioskBet {
    const id = Math.floor(Math.random() * 900_000) + 100_000
    return {
        id,
        qr_code_string: `MOCK-${payload.reunion_id}-C${payload.course_num_ordre}-${id}`,
        is_validated_at_desk: false,
        bet_details: {
            bet_type: payload.bet_type,
            reunion_id: payload.reunion_id,
            reunion_date: payload.reunion_date,
            course_num_ordre: payload.course_num_ordre,
            course_name: payload.course_name,
            stake: payload.stake,
            horses: payload.horses,
        },
        created_at: new Date().toISOString(),
    }
}

/** Simule les pronostics Geny pour la course active avec un tirage aléatoire. */
export function mockGenyPronostic(): GenyPronosticResponse {
    const courses = shiftedCourses()
    const { courseIndex } = config()
    const course = courses[courseIndex - 1]
    const partants = (course.partants ?? []).filter((p) => !p.non_partant)

    // Mélange aléatoire (Fisher-Yates)
    const shuffled = [...partants]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
            ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }

    const toEntries = (slice: typeof shuffled) =>
        slice.map((p, i) => ({
            rangProno: i + 1,
            numeroCheval: Number(p.numero),
            nomCheval: p.cheval,
        }))

    const count = shuffled.length
    const favorisCount = 1
    const opposantsCount = Math.min(3, count - 1)
    const outsidersCount = Math.min(4, count - favorisCount - opposantsCount)
    const delaisseCount = count - favorisCount - opposantsCount - outsidersCount

    return {
        favoris: toEntries(shuffled.slice(0, favorisCount)),
        opposants: toEntries(shuffled.slice(favorisCount, favorisCount + opposantsCount)),
        outsiders: toEntries(shuffled.slice(favorisCount + opposantsCount, favorisCount + opposantsCount + outsidersCount)),
        delaisses: delaisseCount > 0 ? toEntries(shuffled.slice(favorisCount + opposantsCount + outsidersCount)) : null,
    }
}
